import clsx from "clsx";
// Helpers
import {
	gridTreeHasCollapsedRows,
	gridTreeHasExpandedRows,
	gridTreeHasGroups,
	toggleGridTreeRowExpansion,
} from "@/Common/helpers/gridTree";
// MUI
import { useGridApiContext } from "@mui/x-data-grid-pro";
import { IconButton, SvgIcon } from "@mui/material";
// Hooks
import { useCallback, useEffect, useState } from "react";
// Icons
import { ReactComponent as ExpandAllIcon } from "@/Washouts/assets/icons/chevronDown.svg";
import { ReactComponent as CollapseAllIcon } from "@/Washouts/assets/icons/chevronUp.svg";
import { IExpandCollapseAllButtonHeader } from "../types";
import { twMerge } from "tailwind-merge";

/**
 * If props.collapseIfAnyExpanded is true (default), the button will collapse all rows if any row is expanded.
 * If props.collapseIfAnyExpanded is false, the button will collapse all rows if all rows are expanded.
 *
 * If props.isCollapsible is undefined (default), the component will determine the initial status based on props.collapseIfAnyExpanded.
 * If props.isCollapsible is true, the button initial state is to collapse all on click.
 * If props.isCollapsible is false, the button initial state is to expand all on click.
 *
 * Either way the component will subscribe to the rowExpansionChange event to update the internal state taking into consideration props.collapseIfAnyExpanded.
 * @param props: IExpandCollapseAllButtonHeader
 * @returns ReactNode
 */
export function ExpandCollapseAllButtonHeader({
	collapseIfAnyExpanded = true,
	isCollapsible = undefined,
	expandedAll = false,
	className,
	...props
}: Readonly<IExpandCollapseAllButtonHeader>) {
	const selfApiRef = useGridApiContext();
	const apiRef = props.apiRef?.current ? props.apiRef : selfApiRef;
	const [hasGroups, setHasGroups] = useState(true);
	const [collapsible, setCollapsible] = useState(isCollapsible ?? false);
	const predicateFn = collapseIfAnyExpanded ? gridTreeHasExpandedRows : gridTreeHasCollapsedRows;
	const [isSubscribed, setIsSubscribed] = useState(false);

	useEffect(() => {
		if (expandedAll) {
			const SMALL_DELAY_TO_ENSURE_ALL_PENDING_EVENTS_WERE_PROCESSED = 100;
			setTimeout(() => {
				toggleGridTreeRowExpansion(apiRef.current, true);
			}, SMALL_DELAY_TO_ENSURE_ALL_PENDING_EVENTS_WERE_PROCESSED);
		}
	}, [expandedAll, apiRef.current]);

	const expandOrCollapseAll = useCallback(() => {
		toggleGridTreeRowExpansion(apiRef.current, !collapsible);
	}, [collapsible, apiRef]);

	useEffect(() => {
		setHasGroups(gridTreeHasGroups(apiRef.current));

		if (isCollapsible === undefined) {
			setCollapsible(collapseIfAnyExpanded ? predicateFn(apiRef.current) : !predicateFn(apiRef.current));
		}

		if (!isSubscribed) {
			const rowExpansionChangeEvtSubs = apiRef.current.subscribeEvent("rowExpansionChange", () => {
				setCollapsible(collapseIfAnyExpanded ? predicateFn(apiRef.current) : !predicateFn(apiRef.current));
			});
			const rowCountChangeEvtSubs = apiRef.current.subscribeEvent("rowCountChange", () => {
				setHasGroups(gridTreeHasGroups(apiRef.current));
			});
			setIsSubscribed(true);
			return () => {
				rowExpansionChangeEvtSubs();
				rowCountChangeEvtSubs();
			};
		}
		return undefined;
	}, [apiRef, predicateFn, collapseIfAnyExpanded, isCollapsible, setHasGroups]);

	return (
		<IconButton
			className={twMerge("!size-[24px] relative -left-[5px] -top-[3px]", className)}
			tabIndex={-1}
			onClick={expandOrCollapseAll}
			aria-label={collapsible ? "Collapse All" : "Expand All"}
			disabled={!hasGroups}
		>
			<SvgIcon
				className={clsx({
					"!size-[24px]": true,
					"relative left-[4px] top-[7px]": collapsible,
					"relative left-[5px] top-[9px]": !collapsible,
					"[&_svg>path]:fill-[color:--disabled-and-stroke-color]": !hasGroups,
				})}
				viewBox="0 0 30 30"
			>
				{collapsible ? <CollapseAllIcon /> : <ExpandAllIcon />}
			</SvgIcon>
		</IconButton>
	);
}
